import { useState } from 'react'

/**
 * Hook for managing expansion state of signal sections, action sections, and company sections.
 *
 * - signalSectionExpanded: Whether each signal section (unreplied_email, inactive_deal) is expanded
 * - expandedSections: Whether to show all actions in a section (vs first 3)
 * - expandedCompanies: Whether each company section is expanded
 */
export function useExpansionState() {
  // Whether each signal section is expanded (showing its actions)
  const [signalSectionExpanded, setSignalSectionExpanded] = useState<
    Record<string, boolean>
  >({
    unreplied_email: true,
    inactive_deal: true,
  })

  // Whether to show all actions within a section (vs first 3)
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    unreplied_email: false,
    inactive_deal: false,
  })

  const [expandedCompanies, setExpandedCompanies] = useState<
    Record<number, boolean>
  >({})

  /** Toggle whether a signal section (unreplied_email, inactive_deal) is expanded. */
  const toggleSignalSection = (sectionKey: string) => {
    setSignalSectionExpanded((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }

  /** Toggle whether to show all actions in a section (vs first 3). */
  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }

  /** Toggle whether a company section is expanded. */
  const toggleCompany = (companyId: number) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyId]: !prev[companyId],
    }))
  }

  return {
    signalSectionExpanded,
    toggleSignalSection,
    expandedSections,
    toggleSection,
    expandedCompanies,
    toggleCompany,
  }
}
